import {
  type APIMessage,
  can,
  ChannelType,
  PermissionFlags,
} from '@alternativamais/chat-core';
import { useState } from 'react';

import { Avatar } from '../../../componentes/Avatar';
import { avisarErro } from '../../../componentes/Avisos';
import { Interruptor } from '../../../componentes/Interruptor';
import { Icone } from '../../../componentes/Icone';
import { Markdown } from '../../../componentes/Markdown';
import { useCliente, useEstado } from '../../../chat/contexto';
import { formatMessageTime } from '@alternativamais/chat-core';
import { NomeDoAutor } from '../mensagem/Mensagem';

/** Tempo sem atividade para a thread sair da lista (como o Discord). */
export const OCULTAR_APOS = [
  { minutos: 60, rotulo: '1 hora' },
  { minutos: 1440, rotulo: '24 horas' },
  { minutos: 4320, rotulo: '3 dias' },
  { minutos: 10080, rotulo: '1 semana' },
] as const;

/**
 * Nova thread (C35), no painel da direita: a partir de uma mensagem (sempre
 * pública, como no Discord) ou do zero (pode ser privada).
 */
export function NovaThread({
  canalId,
  mensagem,
  aoFechar,
  aoCriar,
}: {
  canalId: string;
  mensagem: APIMessage | null;
  aoFechar: () => void;
  aoCriar: (threadId: string) => void;
}) {
  const cliente = useCliente();
  const guildId = useEstado(
    (estado) => estado.channels[canalId]?.guild_id ?? null,
  );
  const padrao = useEstado(
    (estado) => estado.channels[canalId]?.default_auto_archive_duration ?? 4320,
  );
  const podePrivada = useEstado((estado) =>
    can(estado, canalId, PermissionFlags.CREATE_PRIVATE_THREADS),
  );
  const [nome, setNome] = useState(() =>
    mensagem ? mensagem.content.replace(/\s+/g, ' ').trim().slice(0, 100) : '',
  );
  const [privada, setPrivada] = useState(false);
  const [ocultar, setOcultar] = useState<number>(padrao);
  const [primeira, setPrimeira] = useState('');
  const [criando, setCriando] = useState(false);

  const criar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    const titulo = nome.trim();
    if (!titulo || criando) return;
    setCriando(true);
    try {
      const thread = mensagem
        ? await cliente.api.startThreadFromMessage(canalId, mensagem.id, {
            name: titulo,
            auto_archive_duration: ocultar as 60 | 1440 | 4320 | 10080,
          })
        : await cliente.api.startThread(canalId, {
            name: titulo,
            type: privada
              ? ChannelType.PRIVATE_THREAD
              : ChannelType.PUBLIC_THREAD,
            auto_archive_duration: ocultar as 60 | 1440 | 4320 | 10080,
          });
      cliente.store.getState().upsertChannel(thread);
      if (primeira.trim()) {
        await cliente.sendMessage(thread.id, { content: primeira.trim() });
      }
      aoCriar(thread.id);
    } catch (erro) {
      avisarErro(erro);
      setCriando(false);
    }
  };

  return (
    <aside
      aria-label="Nova thread"
      className="flex w-[420px] max-w-[50%] shrink-0 flex-col border-l border-divisor bg-chat shadow-[-8px_0_24px_rgba(0,0,0,.28)]"
    >
      <header className="flex h-12 shrink-0 items-center gap-2.5 border-b border-divisor pr-2 pl-4">
        <Icone nome="thread" tamanho={20} className="text-texto-3" />
        <h2 className="m-0 grow text-base font-semibold">Nova thread</h2>
        <button
          type="button"
          aria-label="Cancelar nova thread"
          onClick={aoFechar}
          className="flex size-8 items-center justify-center rounded-md text-texto-3 hover:text-texto"
        >
          <Icone nome="x" tamanho={18} />
        </button>
      </header>
      <form
        aria-label={
          mensagem ? 'Criar thread a partir da mensagem' : 'Criar thread'
        }
        onSubmit={criar}
        className="flex min-h-0 grow flex-col"
      >
        <div className="rolagem flex grow flex-col gap-5 overflow-y-auto p-4">
          {mensagem && (
            <section
              aria-label="Mensagem de origem"
              className="flex flex-col gap-1.5 rounded-lg border border-borda bg-lateral p-3"
            >
              <span className="text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
                Mensagem de origem
              </span>
              <div className="flex items-center gap-2">
                <Avatar
                  pessoa={mensagem.author}
                  nome={mensagem.author.global_name ?? mensagem.author.username}
                  tamanho={20}
                />
                <NomeDoAutor autor={mensagem.author} guildId={guildId} />
                <time className="text-xs text-texto-3">
                  {formatMessageTime(mensagem.timestamp)}
                </time>
              </div>
              {mensagem.content && (
                <Markdown
                  texto={mensagem.content}
                  guildId={guildId}
                  className="line-clamp-4 text-sm text-texto-2"
                />
              )}
            </section>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="nome-nova-thread"
                className="text-sm font-semibold text-texto-2"
              >
                Nome da thread
              </label>
              <span className="text-xs text-texto-3">{nome.length}/100</span>
            </div>
            <input
              id="nome-nova-thread"
              autoFocus
              required
              maxLength={100}
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              className="h-10 rounded-lg border border-borda bg-campo px-3 text-[15px] text-texto outline-none focus:border-destaque"
            />
          </div>
          {!mensagem && podePrivada && (
            <div className="flex items-start gap-3">
              <Icone
                nome="cadeado"
                tamanho={20}
                className="mt-0.5 text-texto-3"
              />
              <div className="grow">
                <div
                  id="rotulo-privada-nova"
                  className="text-[15px] font-semibold"
                >
                  Thread privada
                </div>
                <div className="text-sm text-texto-3">
                  Só quem você convidar e os moderadores. Não dá para mudar
                  depois.
                </div>
              </div>
              <Interruptor
                ligado={privada}
                aoMudar={setPrivada}
                rotulo="rotulo-privada-nova"
              />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Icone nome="relogio" tamanho={20} className="text-texto-3" />
            <label
              htmlFor="ocultar-nova"
              className="grow text-[15px] font-semibold"
            >
              Ocultar após inatividade
            </label>
            <select
              id="ocultar-nova"
              value={ocultar}
              onChange={(evento) => setOcultar(Number(evento.target.value))}
              className="h-9 rounded-lg border border-borda bg-campo px-2 text-sm text-texto"
            >
              {OCULTAR_APOS.map((opcao) => (
                <option key={opcao.minutos} value={opcao.minutos}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 border-t border-borda p-4">
          <label htmlFor="primeira-mensagem" className="sr-only">
            Primeira mensagem da thread
          </label>
          <textarea
            id="primeira-mensagem"
            rows={2}
            value={primeira}
            onChange={(evento) => setPrimeira(evento.target.value)}
            placeholder="Escreva a primeira mensagem (opcional)"
            className="resize-none rounded-[10px] bg-campo px-3 py-2.5 text-[15px] text-texto outline-none placeholder:text-texto-3"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-texto-3">
              Quem você @mencionar entra na thread.
            </span>
            <button
              type="submit"
              disabled={!nome.trim() || criando}
              className="h-9 shrink-0 rounded-lg bg-destaque px-4 text-sm font-semibold text-destaque-texto disabled:opacity-50"
            >
              {criando ? 'Criando…' : 'Criar thread'}
            </button>
          </div>
        </div>
      </form>
    </aside>
  );
}

export { Interruptor };
